/**
 * Approval Policy administration page (COMP-005 V7).
 * Allows admins to configure per-tier approval modes, duration limits, and approver groups.
 */

import { useState } from "react";
import { Settings2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "@/components/sc/status-badge";
import { GlassCard } from "@/components/sc/glass-card";
import { EmptyState } from "@/components/sc/empty-state";
import {
  useApprovalPolicies,
  useUpdateApprovalPolicy,
} from "@/hooks/use-approvals";
import { APPROVAL_MODES, type ApprovalPolicy, type ApprovalMode } from "@/types";

const MODE_LABELS: Record<ApprovalMode, string> = {
  self: "Self-Approval",
  single: "1-Person Approval",
  "two-person": "2-Person Approval",
};

export function ApprovalPoliciesPage() {
  const { data, isLoading } = useApprovalPolicies();
  const updateMutation = useUpdateApprovalPolicy();
  const [editTier, setEditTier] = useState<string | null>(null);

  const policies = data?.items ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-sc-pulse text-sc-text-muted">Loading policies...</div>
      </div>
    );
  }

  if (policies.length === 0) {
    return (
      <EmptyState
        icon={<Settings2 size={48} />}
        heading="No policies configured"
        description="Default policies will be used. Click any tier to customize."
      />
    );
  }

  function handleSave(updated: {
    tier: string;
    approvalMode: ApprovalMode;
    maxDurationHours: number;
    requireJustification: boolean;
  }) {
    updateMutation.mutate(
      {
        tier: updated.tier,
        approvalMode: updated.approvalMode,
        maxDurationHours: updated.maxDurationHours,
        requireJustification: updated.requireJustification,
      },
      {
        onSuccess: () => {
          toast.success(`${updated.tier} policy updated`);
          setEditTier(null);
        },
        onError: (err) =>
          toast.error((err as Error).message || "Failed to update policy"),
      },
    );
  }

  return (
    <div className="space-y-3">
      {/* Policy cards — inline editing */}
      {policies.map((policy) => (
        <GlassCard
          key={policy.tier}
          className={editTier === policy.tier ? "border-sc-primary/30" : ""}
        >
          {editTier === policy.tier ? (
            <PolicyEditor
              key={policy.tier}
              policy={policy}
              onSave={handleSave}
              onCancel={() => setEditTier(null)}
              isSaving={updateMutation.isPending}
            />
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <StatusBadge status={policy.tier} category="techTier" />
                <div>
                  <span className="text-body-sm font-medium text-sc-text">
                    {MODE_LABELS[policy.approvalMode] ?? policy.approvalMode}
                  </span>
                  <span className="text-label text-sc-text-muted ml-3">
                    Max {policy.maxDurationHours}h
                  </span>
                  {policy.approverGroupName && (
                    <span className="text-label text-sc-text-muted ml-3">
                      Approvers: {policy.approverGroupName}
                    </span>
                  )}
                  {policy.requireJustification && (
                    <span className="text-label text-sc-text-muted ml-3">
                      Justification required
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => setEditTier(policy.tier)}
                className="px-3 py-1.5 rounded-btn text-body-sm font-medium text-sc-text-muted border border-sc-border hover:bg-sc-surface-hover transition-colors"
              >
                Edit
              </button>
            </div>
          )}
        </GlassCard>
      ))}
    </div>
  );
}

interface PolicyEditorProps {
  readonly policy: ApprovalPolicy;
  readonly onSave: (updated: {
    tier: string;
    approvalMode: ApprovalMode;
    maxDurationHours: number;
    requireJustification: boolean;
  }) => void;
  readonly onCancel: () => void;
  readonly isSaving: boolean;
}

function PolicyEditor({ policy, onSave, onCancel, isSaving }: PolicyEditorProps) {
  const [mode, setMode] = useState<ApprovalMode>(policy.approvalMode);
  const [maxHours, setMaxHours] = useState(policy.maxDurationHours);
  const [requireJust, setRequireJust] = useState(policy.requireJustification);

  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <StatusBadge status={policy.tier} category="techTier" />
        <span className="text-body-sm font-medium text-sc-text">
          Editing Policy
        </span>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <label className="block text-label font-medium text-sc-text-muted uppercase tracking-wider mb-1">
            Approval Mode
          </label>
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value as ApprovalMode)}
            className="w-full h-9 px-3 rounded-input border border-sc-border bg-sc-input-bg text-sc-text text-body-sm"
          >
            {APPROVAL_MODES.map((m) => (
              <option key={m} value={m}>
                {MODE_LABELS[m]}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-label font-medium text-sc-text-muted uppercase tracking-wider mb-1">
            Max Duration (hours)
          </label>
          <input
            type="number"
            min={1}
            max={24}
            value={maxHours}
            onChange={(e) => setMaxHours(Number(e.target.value))}
            className="w-full h-9 px-3 rounded-input border border-sc-border bg-sc-input-bg text-sc-text text-body-sm"
          />
        </div>

        <div className="flex items-end pb-1">
          <label className="flex items-center gap-2 text-body-sm text-sc-text cursor-pointer">
            <input
              type="checkbox"
              checked={requireJust}
              onChange={(e) => setRequireJust(e.target.checked)}
              className="h-4 w-4 rounded border-sc-border text-sc-primary focus:ring-sc-primary"
            />
            Require Justification
          </label>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-3">
        <button
          onClick={() =>
            onSave({
              tier: policy.tier,
              approvalMode: mode,
              maxDurationHours: maxHours,
              requireJustification: requireJust,
            })
          }
          disabled={isSaving}
          className="flex items-center gap-1 px-3 py-1.5 rounded-btn text-body-sm font-medium bg-sc-primary text-white hover:bg-sc-primary/90 disabled:opacity-50 transition-colors"
        >
          <Check size={14} />
          {isSaving ? "Saving..." : "Save"}
        </button>
        <button
          onClick={onCancel}
          className="flex items-center gap-1 px-3 py-1.5 rounded-btn text-body-sm text-sc-text-muted hover:text-sc-text transition-colors"
        >
          <X size={14} />
          Cancel
        </button>
      </div>
    </div>
  );
}
